<!-- Modern Route Generator Component -->
<template>
  <div class="relative bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden shadow-lg" :style="'height:' + height">

    <!-- Loading Overlay -->
    <div v-if="load" class="absolute inset-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm z-50 flex items-center justify-center">
      <div class="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 text-center max-w-sm mx-4">
        <!-- Spinner with pulse effect -->
        <div class="relative mb-4">
          <div class="w-12 h-12 border-4 border-gray-200 dark:border-gray-600 rounded-full animate-spin border-t-red-600 mx-auto"></div>
          <div class="absolute inset-0 w-12 h-12 border-4 border-transparent rounded-full animate-ping border-t-red-600/30 mx-auto"></div>
        </div>
        
        <!-- Title and description -->
        <div class="space-y-2">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">
            {{ compactMode ? 'Hesaplanıyor' : 'Rota Hesaplanıyor' }}
          </h3>
          <p v-if="!compactMode" class="text-sm text-gray-500 dark:text-gray-400">
            En uygun rota bulunuyor...
          </p>
        </div>
        
        <!-- Progress dots -->
        <div class="flex justify-center space-x-1 mt-4">
          <div class="w-2 h-2 bg-red-600 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
          <div class="w-2 h-2 bg-red-600 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
          <div class="w-2 h-2 bg-red-600 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
        </div>
      </div>
    </div>

    <!-- Minimal Control Panel -->
    <div v-if="!load" class="absolute top-3 left-3 right-3 z-40 flex items-start justify-between">
      <!-- Left Controls -->
      <div class="flex items-center space-x-2">
        <!-- Map View Toggle -->
        <button
          type="button"
          @click="$store.commit('toggleMapStatus')"
          class="h-10 rounded-lg shadow-md transition-all duration-200 hover:shadow-lg flex items-center space-x-2 px-3"
          :class="$store.state.mapView ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200'"
          :title="$store.state.mapView ? 'Yol Görünümü' : 'Uydu Görünümü'"
        >
          <img
            class="w-5 h-5"
            :src="$store.state.mapView 
              ? require('@/assets/marker/mapstatus_white.svg')
              : require('@/assets/marker/mapstatus.svg')"
          />
          <span class="text-xs font-medium">
            {{ $store.state.mapView ? 'Uydu' : 'Yol' }}
          </span>
        </button>

      </div>

      <!-- Right Controls -->
      <div class="flex items-center space-x-2">
        <!-- Route Info Summary -->
        <div v-if="route && route.kilometer" class="h-10 bg-white border border-gray-200 rounded-lg shadow-lg px-3 flex items-center">
          <div class="flex items-center space-x-3 text-xs">
            <span class="text-gray-700 font-medium">{{ route.kilometer }}km</span>
            <span class="text-gray-400">•</span>
            <span class="text-gray-700 font-medium">{{ getTimeDetail(route.totalMinutes) }}</span>
            <span class="text-gray-400">•</span>
            <span class="text-green-600 font-semibold">{{ Money(route.totalCost + fuelPrice) }}₺</span>
          </div>
        </div>

        <!-- Full Detail Toggle -->
        <button
          type="button"
          @click="showFullDetail = !showFullDetail"
          class="h-10 bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-800 px-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center space-x-2"
          title="Detaylı rota bilgilerini göster"
        >
          <i class="fas fa-expand-alt text-sm"></i>
          <span class="text-xs font-medium">Detaylar</span>
        </button>
      </div>
    </div>

    <!-- Full Detail Panel -->
    <transition name="slide-right">
      <fullViewPanel
        v-if="showFullDetail"
        @hidePanel="showFullDetail = false"
        :endPosition="endPosition"
        :startPosition="startPosition"
        :polyLine="polyLine"
        :forbidenBounds="forbidenBounds"
        :avoidPointsList="avoidPointsList"
        :waypoints="waypoints"
        :tolls="tolls"
        :routes="route"
        :bridges="bridges"
        :isExplosiveDefault="explosiveState"
        :fuelPrice="fuelPrice"
        :initialVehicleTypeId="vehicleTypeId"
        :initialRouteType="routeType === 'fast' ? 1 : 2"
        :initialIsToll="isToll"
        :initialIsShip="isShip"
        :initialFuelRatio="fuelRatio"
        @change="(r) => $emit('change', r)"
        @changeStartPosition="(val) => $emit('changeStartPosition', val)"
        @changeEndPosition="(val) => $emit('changeEndPosition', val)"
        @changeOutpoint="(r) => $emit('changeOutpoint', r)"
        @changeCustomer="(r) => $emit('changeCustomer', r)"
        :customerId="customer"
        :outpointId="outpoint"
        :custompoints="customPoints"
        @recalculate="getRoute"
      />
    </transition>


    <!-- Google Map -->
    <GmapMap
      v-if="!load && startPosition.lat && startPosition.lng && endPosition.lat && endPosition.lng && route && route.kilometer"
      @click="handleMapClick"
      :center.sync="center"
      :zoom="zoomLevel"
      :map-type-id="$store.state.mapView ? 'hybrid' : 'roadmap'"
      :options="mapOptions"
      :style="'height:' + height"
      class="w-full rounded-xl"
    >
      <!-- Vehicle Markers -->
      <GmapMarker
        v-for="m in markers"
        :key="'vehicle-' + m.id"
        :title="m.title"
        :position="m.position"
        :icon="m.icon"
        :label="vehicleLabel(m)"
      />

      <!-- Start Position -->
      <GmapMarker 
        :position="startPosition" 
        @click="center = startPosition"
        :icon="startMarkerIcon"
        :label="startLabel"
      />

      <!-- Waypoints -->
      <GmapMarker
        v-for="(item, index) in waypoints"
        v-show="item.no"
        :key="'waypoint-' + index"
        :position="item"
        :icon="getWaypointIcon(item.no)"
        :label="getWaypointLabel(item)"
        @click="handleWaypointClick(item, index)"
      />

      <!-- End Position -->
      <GmapMarker
        :position="endPosition"
        :icon="endMarkerIcon"
        :label="endLabel"
        @click="center = endPosition"
      />

      <!-- Bridges -->
      <GmapMarker
        v-for="(item, index) in bridges"
        :key="'bridge-' + index"
        :icon="bridgeIcon"
        :position="{ lat: item.startLat, lng: item.startLng }"
        :label="getBridgeLabel(item)"
        @click="handleBridgeClick(item)"
      />

      <!-- Custom Points -->
      <GmapMarker
        v-for="(item, index) in customPoints"
        :key="'custom-' + index"
        :icon="customPointIcon"
        :position="{ lat: Number(item.latitude), lng: Number(item.longitude) }"
        :label="getCustomPointLabel(item)"
        @click="handleCustomPointClick(item)"
      />

      <!-- Toll Gates -->
      <div v-for="(item, index) in tolls" :key="'toll-' + index">
        <!-- Entrance -->
        <GmapMarker
          v-if="item.typeId === 1"
          :position="{ lat: item.startLat, lng: item.startLng }"
          :icon="tollIcon"
          :label="getTollLabel(item, 'Giriş')"
          @click="handleTollClick(item, 'entrance')"
        />
        
        <!-- Exit -->
        <GmapMarker
          v-if="item.typeId === 1"
          :position="{ lat: item.endLat, lng: item.endLng }"
          :icon="tollIcon"
          :label="getTollLabel(item, 'Çıkış')"
          @click="handleTollClick(item, 'exit')"
        />
        
        <!-- Barriers -->
        <GmapMarker
          v-if="item.typeName === 2"
          :position="{ lat: item.startLat, lng: item.startLng }"
          :icon="barrierIcon"
          :label="getBarrierLabel(item)"
          @click="handleBarrierClick(item)"
        />
      </div>

      <!-- Route Polyline -->
      <GmapPolyline
        v-if="polyLine.length"
        :path="polyLine"
        :options="polylineOptions"
      />
    </GmapMap>

    <!-- Empty State - No Route Data -->
    <div v-else-if="!load && (!startPosition.lat || !startPosition.lng || !endPosition.lat || !endPosition.lng || !route || !route.kilometer)" class="flex items-center justify-center" :style="'height:' + height">
      <div class="text-center">
        <i class="fas fa-map-marked-alt text-4xl text-gray-400 mb-4"></i>
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Rota Belirleyin</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">Başlangıç ve bitiş noktalarını seçerek rotayı hesaplayın</p>
      </div>
    </div>

    <!-- No Route State -->
   
  </div>
</template>

<script>
import axios from "axios";
import { routeEngine } from "@/networking/routeEngine";
import fullViewPanel from "./fullView.vue";
import { decode } from "../../../utils/encoder";
import { managementCosting } from "../../../networking/urlmanager";
import { getLocationAll } from "../../../utils/detailData";

export default {
  name: "route-viewer",
  props: {
    center: Object,
    height: String,
    startPosition: Object,
    endPosition: Object,
    routeString: String,
    editState: Boolean,
    explosiveState: [Number, Boolean],
    customer: [String, Number],
    outpoint: [String, Number],
    waypointsList: Array,
    vehicle: Object,
    compactMode: Boolean,
    // Rota ayarları (opsiyonel - yoksa default değerler kullanılır)
    initialVehicleTypeId: {
      type: Number,
      default: 5 // TIR
    },
    initialRouteType: {
      type: String,
      default: "fast" // "fast" or "short"
    },
    initialIsToll: {
      type: Number,
      default: 1 // Ücretli yollar kullan
    },
    initialIsShip: {
      type: Number,
      default: 1 // Feribot kullan
    },
    initialIsOptimized: {
      type: Number,
      default: 1 // Optimize edilmiş rota
    },
  },
  components: {
    fullViewPanel,
  },
  data() {
    return {
      markers: [],
      route: {},
      activeRoute: 0,
      waypoints: [],
      customPoints: [],
      bridges: [],
      tolls: [],
      polyLine: [],
      avoidPointsList: [],
      forbidenBounds: null,
      load: true,
      showFullDetail: false,
      fuelPrice: 0,
      zoomLevel: 8,
      localCenter: this.center,

      // Route Settings - Reactive
      vehicleTypeId: 5, // TIR (default)
      routeType: "fast", // "fast" or "short"
      isToll: 1, // Ücretli yollar
      isShip: 1, // Feribot kullanımı
      isOptimized: 1, // Optimize edilmiş rota
      fuelRatio: 35, // Yakıt tüketim oranı

      // Icon configurations
      locationPinImages: [
        require("@/assets/marker/location-pin.png"),
        require("@/assets/marker/location-pin-2.png"),
        require("@/assets/marker/location-pin-3.png"),
        require("@/assets/marker/location-pin-4.png"),
        require("@/assets/marker/location-pin-5.png"),
      ],
    };
  },
  computed: {
    mapOptions() {
      return {
        minZoom: 6,
        maxZoom: 18,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        disableDefaultUi: false,
        gestureHandling: 'greedy',
        styles: this.$store.state.mapView ? [] : [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }]
          }
        ]
      };
    },
    polylineOptions() {
      return {
        strokeColor: '#3B82F6',
        strokeWeight: 4,
        strokeOpacity: 0.8,
        editable: false,
        draggable: false
      };
    },
    startMarkerIcon() {
      return {
        url: require("@/assets/marker/location-pin.png"),
        scaledSize: { width: 32, height: 32 },
      };
    },
    endMarkerIcon() {
      return {
        url: require("@/assets/marker/flag.png"),
        scaledSize: { width: 32, height: 32 },
      };
    },
    bridgeIcon() {
      return {
        url: require("@/assets/marker/bridge.png"),
        scaledSize: { width: 24, height: 24 },
      };
    },
    customPointIcon() {
      return {
        url: require("@/assets/marker/customspoints.png"),
        scaledSize: { width: 24, height: 24 },
      };
    },
    tollIcon() {
      return {
        url: require("@/assets/marker/hgs.png"),
        scaledSize: { width: 24, height: 24 },
      };
    },
    barrierIcon() {
      return {
        url: require("@/assets/marker/barrier.png"),
        scaledSize: { width: 24, height: 24 },
      };
    },
    startLabel() {
      return {
        text: 'Başlangıç',
        color: '#374151',
        className: 'marker-label-modern',
        fontSize: '11px',
      };
    },
    endLabel() {
      return {
        text: 'Hedef',
        color: '#374151',
        className: 'marker-label-modern',
        fontSize: '11px',
      };
    },
  },
  methods: {
    Money(value) {
      return Number(value).toFixed(2);
    },
    getTimeDetail(minutes) {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return `${hours}s ${mins}dk`;
    },
    vehicleLabel(vehicle) {
      return {
        text: vehicle.title,
        color: '#374151',
        className: 'marker-label-modern',
        fontSize: '10px',
      };
    },
    getWaypointIcon(no) {
      return {
        url: this.locationPinImages[(no - 1) % 5],
        scaledSize: { width: 28, height: 28 },
      };
    },
    getWaypointLabel(waypoint) {
      return {
        text: `${waypoint.no}. Durak`,
        color: '#374151',
        className: 'marker-label-modern',
        fontSize: '10px',
      };
    },
    getBridgeLabel(bridge) {
      return {
        text: bridge.name,
        color: '#374151',
        className: 'marker-label-modern',
        fontSize: '10px',
      };
    },
    getCustomPointLabel(point) {
      return {
        text: point.name,
        color: '#374151',
        className: 'marker-label-modern',
        fontSize: '10px',
      };
    },
    getTollLabel(toll, type) {
      return {
        text: `${type}: ${toll.name}`,
        color: '#374151',
        className: 'marker-label-modern',
        fontSize: '10px',
      };
    },
    getBarrierLabel(barrier) {
      return {
        text: barrier.name,
        color: '#374151',
        className: 'marker-label-modern',
        fontSize: '10px',
      };
    },
    handleMapClick() {
      if (!this.showFullDetail) {
        this.showFullDetail = true;
      }
    },
    handleWaypointClick(waypoint) {
      this.updateCenter({ lat: waypoint.lat, lng: waypoint.lng });
      this.zoomLevel = 14;
    },
    handleBridgeClick(bridge) {
      this.updateCenter({ lat: bridge.startLat, lng: bridge.startLng });
      this.zoomLevel = 14;
    },
    handleCustomPointClick(point) {
      this.updateCenter({ lat: Number(point.latitude), lng: Number(point.longitude) });
      this.zoomLevel = 14;
    },
    handleTollClick(toll, type) {
      const lat = type === 'entrance' ? toll.startLat : toll.endLat;
      const lng = type === 'entrance' ? toll.startLng : toll.endLng;
      this.updateCenter({ lat, lng });
      this.zoomLevel = 14;
    },
    handleBarrierClick(barrier) {
      this.updateCenter({ lat: barrier.startLat, lng: barrier.startLng });
      this.zoomLevel = 14;
    },
    changeRoute() {
      // Implementation for alternative routes
      if (Array.isArray(this.route) && this.route.length > 1) {
        this.activeRoute = (this.activeRoute + 1) % this.route.length;
        this.routeGenerate();
      }
    },
    updateCenter(newCenter) {
      this.localCenter = newCenter;
      this.$emit("update:center", newCenter);
    },
    getAll(load = true) {
      this.markers = [];
      this.load = load;
      
      getLocationAll(this.$store.state.userData.token)
        .then((res) => {
          this.load = false;
          if (Array.isArray(res) && res.length > 0) {
            let markers = res.filter(
              (r) => r.vehicle?.toLowerCase() === this.vehicle?.toLowerCase().replace(/\s/g, "")
            );
            
            if (markers.length > 0) {
              this.updateCenter({
                lat: markers[0]?.latitude,
                lng: markers[0]?.longitude,
              });
              
              this.markers = markers.map((el) => ({
                id: el.id,
                title: el.vehicle,
                region: el.region,
                position: {
                  lat: parseFloat(el?.latitude),
                  lng: parseFloat(el?.longitude),
                },
                adress: el.address,
                vehicle: el.vehicle,
                dorse: el.dorse,
                planning: el.planning,
                state: el.state,
                speed: el.speed,
                icon: this.positionMarker(el.speed),
              }));
            }
          }
        })
        .catch((err) => {
          this.load = false;
          console.error("Vehicle location fetch error:", err);
        });
    },
    positionMarker(speed) {
      if (speed > 5) {
        return {
          url: require("@/assets/marker/yellow-truck.png"),
          scaledSize: { width: 32, height: 32 },
        };
      } else {
        return {
          url: require("@/assets/marker/red-truck.png"),
          scaledSize: { width: 32, height: 32 },
        };
      }
    },
    getRoute(loadState = true) {
      this.activeRoute = 0;
      this.load = loadState;

      // Araç tipine göre yakıt oranını ayarla
      const fuelRatioMap = {
        1: 20,  // Otomobil
        2: 25,  // Minivan
        3: 30,  // Kamyonet
        4: 32,  // Kamyon
        5: 35,  // TIR
      };
      this.fuelRatio = fuelRatioMap[this.vehicleTypeId] || 35;

      axios.post(routeEngine.generate, {
        from: {
          lat: this.startPosition.lat,
          lng: this.startPosition.lng,
        },
        to: {
          lat: this.endPosition.lat,
          lng: this.endPosition.lng,
        },
        waypoints: this.waypoints,
        isToll: this.isToll, // Reactive
        vehicleTypeId: this.vehicleTypeId, // Reactive - Default TIR (5)
        routeType: this.routeType, // Reactive - Default "fast"
        shippedHazardousGoods: this.explosiveState,
        isShip: this.isShip, // Reactive
        isAsphalt: 1,
        fuelRatio: this.fuelRatio, // Reactive - Araç tipine göre
        isPolyline: 1,
        isApi: 1,
        isOptimized:1
      }, {
        headers: {
          Authorization: "Bareer " + this.$store.state.routeEngineToken,
        },
      })
      .then((res) => {
        if (res.data && res.data.data && res.data.data.kilometer) {
          this.route = res.data.data;
          this.routeGenerate();
          this.getManagementCost(res.data.data.kilometer);
          this.getAll(false); // Vehicle loading'i ayrı yap
        } else {
          throw new Error("Invalid route data received");
        }
      })
      .catch((err) => {
        console.error("Route generation error:", err);
        // Sadece gerçek hata durumunda toast göster
        if (err.response && err.response.status !== 200) {
          this.$toast.error("Rota hesaplanırken bir hata oluştu.");
        }
      })
      .finally(() => {
        this.load = false;
      });
    },
    async getManagementCost(km) {
      try {
        const res = await axios.get(managementCosting.getAll, {
          headers: {
            Authorization: "Bareer " + this.$store.state.userData.token,
          },
        });
        const kilometerForFuel = 0.34;
        const fuelPrice = Number(res.data.data[20].price.replace(",", "."));
        this.fuelPrice = km * kilometerForFuel * fuelPrice;
      } catch (err) {
        console.error("Fuel cost calculation error:", err);
      }
    },
    routeGenerate() {
      const data = this.route;
      this.polyLine = decode(data.routePolyline).polyline;
      this.$emit("changePolyline", this.polyLine);

      this.customPoints = Array.isArray(data?.customPoints) ? data?.customPoints : [];

      const parsedCostDetail = data.costDetail.map((item) => ({
        ...item,
        startLat: Number(item.startLat),
        startLng: Number(item.startLng),
        endLat: Number(item.endLat),
        endLng: Number(item.endLng),
      }));

      this.tolls = parsedCostDetail.filter(item => item.typeName !== "Bridge");
      this.bridges = parsedCostDetail.filter(item => item.typeName === "Bridge");

      // Auto-adjust zoom to fit route
      if (this.polyLine.length > 0) {
        this.fitToRoute();
      }

      this.$emit("change", {
        cost: {
          fuel: this.fuelPrice,
          cash: data.totalCost,
        },
        polyLine: JSON.stringify({
          startPosition: this.startPosition,
          waypoints: this.waypoints,
          endPosition: this.endPosition,
        }),
        totalMinutes: data.totalMinutes,
        navigationLink: data.navigationLink,
        totalKilometer: data.kilometer,
        tolls: data.tolls,
        bridges: data.bridges,
      });
    },
    fitToRoute() {
      // Calculate bounds to fit all route points
      if (this.polyLine.length > 0) {
        // Google Maps API kontrolü
        if (typeof google === 'undefined' || !google.maps) {
          console.warn('Google Maps API not loaded yet, skipping fitToRoute');
          return;
        }

        try {
          const bounds = new google.maps.LatLngBounds();
          this.polyLine.forEach(point => bounds.extend(point));

          // Add some padding
          this.updateCenter(bounds.getCenter().toJSON());
          this.zoomLevel = 8; // Reasonable default zoom
        } catch (error) {
          console.warn('Error fitting route to bounds:', error);
        }
      }
    },
  },
  created() {
    // Props'tan gelen initial değerleri data'ya set et
    if (this.initialVehicleTypeId) {
      this.vehicleTypeId = this.initialVehicleTypeId;
    }
    if (this.initialRouteType) {
      this.routeType = this.initialRouteType;
    }
    if (this.initialIsToll !== undefined) {
      this.isToll = this.initialIsToll;
    }
    if (this.initialIsShip !== undefined) {
      this.isShip = this.initialIsShip;
    }
    if (this.initialIsOptimized !== undefined) {
      this.isOptimized = this.initialIsOptimized;
    }

    if (Array.isArray(this.waypointsList)) {
      this.waypoints = this.waypointsList.map((item) => ({
        no: item.no,
        adress: item.position.adress,
        lat: item.position.lat,
        lng: item.position.lng,
      }));
    }

    if (this.startPosition.lat && this.endPosition.lat) {
      this.load = true;

      this.getRoute();
    } else {
      // Eğer rota yoksa loading'i kapat
      this.load = false;
    }
  },
  watch: {
    waypointsList(val) {
      if (Array.isArray(val)) {
        this.waypoints = val.map((item) => ({
          no: item.no,
          isWaypoint: item.isWaypoint,
          adress: item.position.adress,
          lat: item.position.lat,
          lng: item.position.lng,
        }));
        this.getRoute();
      }
    },
    load(val) {
      this.$emit("toggleRouteLoad", val);
    },
    center(newValue) {
      this.localCenter = newValue;
    },
  },
};
</script>

<style scoped>
/* Modern Marker Label Styles */
:global(.marker-label-modern) {
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 10px;
  font-weight: 500;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 5px;
}

/* Transition Animations */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease;
}

.slide-right-enter,
.slide-right-leave-to {
  transform: translateX(100%);
}

/* Custom Map Container */
.route-map-container {
  border-radius: 12px;
  overflow: hidden;
}

/* Loading Animation */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>